import { useAutocomplete } from '@authoritas/react-autocomplete'
import { useDropdown } from '@authoritas/react-dropdown'
import URI from 'urijs'
import { useCallback, useEffect, useState } from 'react'
import validate from 'validate.js'

const useVeSearchForm = (availableMarkets, v6Address = window.location.origin, defaultMarket = 'en_us') => {
    const [redirectionUrl, setRedirectionUrl] = useState(null)
    const [validationErrors, setValidationErrors] = useState(null)

    const splitLanguage = navigator.language.split('-')
    const userLocale = `${splitLanguage[0]}_${splitLanguage[1] ?? splitLanguage[0]}`.toLowerCase()
    const initialMarket = availableMarkets.find(market => market === userLocale) || defaultMarket

    const {
        getWrapperProps: getMarketDropdownWrapperProps,
        getTriggerProps: getMarketDropdownTriggerProps,
        getItemProps: getMarketDropdownItemProps,
        isVisible: isMarketDropdownVisible,
        highlightedItem: highlightedMarket,
        selectedItem: selectedMarket,
    } = useDropdown(availableMarkets, initialMarket)

    const protocolRegex = new RegExp('^http(s)?://', 'i')
    const enforceProtocol = value => protocolRegex.test(value) ? value : `https://${value}`
    const extractHostFromUrl = url => {
        const urlWithProtocol = enforceProtocol(url)
        const uri = new URI(urlWithProtocol)
        return uri.hostname()
    }

    const veAutocompleteSuggestionProvider = async (url, selectedMarket) => (
        await fetch(`${v6Address}/api/v2/public/ve/autocomplete/${selectedMarket}/${extractHostFromUrl(url)}`)
    ).json()

    const toggleWwwPrefix = useCallback(host => {
        const prependWww = () => `www.${host}`
        const removeWwwPrefix = () => host.slice(4)
        const hasWwwPrefix = () => host.startsWith('www.')

        return hasWwwPrefix() ? removeWwwPrefix() : prependWww()
    }, [])

    const {
        getWrapperProps: getAutocompleteWrapperProps,
        getInputProps: getAutocompleteInputProps,
        getItemProps: getAutocompleteSuggestionProps,
        isLoading: isAutocompleteLoading,
        isVisible: isAutocompleteSuggestionContainerVisible,
        highlightedItem: highlightedAutocompleteSuggestion,
        selectedItem: selectedAutocompleteSuggestion,
        suggestions: autocompleteSuggestions,
        setInput: setAutocompleteInput,
        input: autocompleteInput,
    } = useAutocomplete(
        veAutocompleteSuggestionProvider,
        input => suggestion => suggestion.startsWith(extractHostFromUrl(input)) ||
            suggestion.startsWith(extractHostFromUrl(toggleWwwPrefix(input))),
        selectedMarket,
    )

    useEffect(() => {
        if (autocompleteInput?.toLocaleLowerCase() === autocompleteInput) {
            const input = enforceProtocol(autocompleteInput)
            const constrains = {
                input: {
                    url: true,
                },
            }
            setValidationErrors(autocompleteInput?.length > 0 ? validate({ input }, constrains)?.input ?? [] : null)
        } else {
            autocompleteInput?.length > 0 && setAutocompleteInput(autocompleteInput.toLocaleLowerCase())
        }
    }, [autocompleteInput])

    useEffect(() => {
        const suggestion = selectedAutocompleteSuggestion === autocompleteInput
            ? selectedAutocompleteSuggestion
            : extractHostFromUrl(autocompleteInput)
        setRedirectionUrl(`${v6Address}/en_GB/competitive-analysis/ve/${selectedMarket}/${suggestion}`)
    }, [selectedMarket, selectedAutocompleteSuggestion, autocompleteInput])

    useEffect(() => {
        setAutocompleteInput(selectedAutocompleteSuggestion)
    }, [selectedAutocompleteSuggestion])

    const getSubmitButtonProps = () => ({
        onClick: () => validationErrors?.length === 0 && redirectionUrl && (window.location = redirectionUrl),
    })

    return {
        getMarketDropdownWrapperProps,
        getMarketDropdownTriggerProps,
        getMarketDropdownItemProps,
        isMarketDropdownVisible,
        highlightedMarket,
        selectedMarket,
        getAutocompleteWrapperProps,
        getAutocompleteInputProps: () => ({
            ...getAutocompleteInputProps(),
            autoCorrect: 'off',
            autoCapitalize: 'none',
        }),
        getAutocompleteSuggestionProps,
        isAutocompleteLoading,
        isAutocompleteSuggestionContainerVisible,
        highlightedAutocompleteSuggestion,
        selectedAutocompleteSuggestion,
        autocompleteSuggestions,
        getSubmitButtonProps,
        validationErrors,
    }
}

export default useVeSearchForm
